import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

import LessonInfo from '../LessonInfo/LessonInfo';
import MissingActivities from 'components/UI/MissingActivities/MissingActivities';
import classes from './CourseContent.module.css';

/**
 * CourseContent Component
 *
 * A comprehensive React component that displays the course content structure, including lessons organized
 * in separators/folders and standalone lessons. The component adapts its behavior and UI based on the user's access level:
 * - With access: Shows missing activities and interactive lesson items
 * - Without access: Shows read-only course structure
 *
 * Features:
 * - Dynamic rendering of course lessons and folders
 * - Accordion UI for grouped lessons
 * - Integration with user progress tracking
 * - Multi-language support via i18n
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.lessons - Course lessons structure containing separators and lessons
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Object} props.userTrack - User progress tracking data for lessons
 * @returns {JSX.Element} The rendered course content section
 */

const CourseContent = props => {
  const { t } = useTranslation(['course']);
  return (
    <>
      <div
        className={
          props.accessCourse
            ? classes.productLearnInfoAccessSection
            : classes.productLearnInfoSection
        }
      >
        <h2 className={classes.includesTitle}>
          {t('contents')} {/* Internationalized section title */}
        </h2>
        {/* Show missing activities component only for users with course access */}
        {props.accessCourse && (
          <MissingActivities /* No props, uses global state */ />
        )}
        <aside className={classes.productContentItemsContainer}>
          {/* Render lessons organized in separators (folders) as expandable accordions */}
          {props.lessons.separators.map(separator => (
            <Accordion
              className={classes.productContentPanel}
              key={separator.separator.idSeparator}
            >
              <AccordionSummary
                className={classes.productContentTitle}
                expandIcon={
                  <ExpandMoreIcon
                    className={classes.productContentPanelIcon}
                  />
                }
              >
                <Typography
                  className={classes.productContentPanelTitle}
                >
                  {separator.separator.name}{' '}
                  {/* Folder name passed as prop to Typography */}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                className={classes.lessonAcordionDetails}
              >
                {/* Render each lesson within the separator */}
                {separator.lessons.map(lesson => (
                  <LessonInfo
                    key={lesson.idVideo}
                    data={lesson} // Lesson data passed as prop
                    accessCourse={props.accessCourse} // User access status passed as prop
                    userTrack={props.userTrack} // User progress tracking passed as prop
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
          {/* Render standalone lessons (not in separators) */}
          {props.lessons.lessons.map(lesson => (
            <LessonInfo
              key={lesson.idVideo}
              data={lesson} // Lesson data passed as prop
              accessCourse={props.accessCourse} // User access status passed as prop
              userTrack={props.userTrack} // User progress tracking passed as prop
            />
          ))}
        </aside>
      </div>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CourseContent.propTypes = {
  /**
   * Course lessons structure containing two main arrays:
   * - separators: Array of lesson groups/folders with nested lessons
   * - lessons: Array of standalone lessons not grouped in separators
   */
  lessons: PropTypes.shape({
    separators: PropTypes.arrayOf(
      PropTypes.shape({
        separator: PropTypes.shape({
          idSeparator: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
          ]).isRequired,
          name: PropTypes.string.isRequired // Folder name used in child Typography
        }).isRequired,
        lessons: PropTypes.arrayOf(
          PropTypes.shape({
            idVideo: PropTypes.oneOfType([
              PropTypes.string,
              PropTypes.number
            ]).isRequired // Lesson ID used as key in child LessonInfo
          })
        ).isRequired
      })
    ),
    lessons: PropTypes.arrayOf(
      PropTypes.shape({
        idVideo: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number
        ]).isRequired // Lesson ID used as key in child LessonInfo
      })
    )
  }),
  /**
   * Boolean indicating whether the user has access to the course content
   * When true, shows missing activities component and enables interactive features
   * Passed as prop to child LessonInfo components
   */
  accessCourse: PropTypes.bool,
  /**
   * User progress tracking data containing completion status for lessons
   * Used by LessonInfo components to display progress indicators
   */
  userTrack: PropTypes.object
};

CourseContent.defaultProps = {
  lessons: { separators: [], lessons: [] },
  accessCourse: false,
  userTrack: {}
};

export default CourseContent;
